import { Box, Flex, Text, Image } from "@chakra-ui/react";
import { useUserInfo } from "@/context/UserInfo";
import { useMemo, useState } from "react";
import { ethAddress, usdcInfo } from "@/constants/tokens";
import { formatAmountWithFixed } from "@/utils/formatBalance";
import { useCurrencyExchange } from "@/hooks/useCurrencyExchange";
import CurrencySwitch from "@/components/CurrencySwitch";
import { BottomSheet, BottomSheetRef } from "react-spring-bottom-sheet";
import "react-spring-bottom-sheet/dist/style.css";
import "./bottomStyle.css";
import { useEffect, useRef } from "react";
import { defaultSnapProps } from "react-spring-bottom-sheet/dist/types";

const BalanceContent = ({
  isActive,
  sheetRef,
  overlayOpacity,
  handleOnOverlayOpacity,
}: {
  isActive: boolean;
  overlayOpacity: number;
  sheetRef: React.RefObject<BottomSheetRef>;
  handleOnOverlayOpacity: (opacity: number) => void;
}) => {
  const {
    totalUsdValue,
    tokenInfo,
    showUsd,
    handleSwitchCurrency,
    showBalance,
  } = useUserInfo();
  const { currencyUnit, usdToCurrency } = useCurrencyExchange();

  const balanceList = useMemo(() => {
    const list = Object.values(tokenInfo)
      .filter((token) => token.balance !== "0" || token.address === ethAddress)
      .map((token) => ({
        ...token,
      }));
    // 将ETH放在第一位
    list.sort((a, b) => {
      if (a.symbol === "ETH") return -1;
      if (b.symbol === "ETH") return 1;
      return 0;
    });
    return list;
  }, [tokenInfo]);

  // 解决BottomSheet 内容不能拖动的bug
  useEffect(() => {
    const host = document.querySelector("wcm-modal");
    let elementInsideShadow: HTMLElement | null = null;
    if (host?.shadowRoot) {
      elementInsideShadow = host.shadowRoot.querySelector(
        "#wcm-modal"
      ) as HTMLElement;
      if (elementInsideShadow) {
        elementInsideShadow.style.display = "none";
      }
    }
    return () => {
      if (elementInsideShadow) {
        elementInsideShadow.style.display = "flex";
      }
    };
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      const sheet = sheetRef.current;
      if (!sheet) return;

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          const maxHeight = window.innerHeight;
          const quarterHeight = Math.ceil((maxHeight / 10) * 3) + 10;
          if (height <= quarterHeight) {
            handleOnOverlayOpacity(0);
          } else if (height >= quarterHeight + 50) {
            handleOnOverlayOpacity(1);
          } else {
            const opacity = (height - quarterHeight) / 50;
            handleOnOverlayOpacity(opacity);
          }
        }
      });

      const element = document.querySelector(
        'div[role="dialog"][aria-modal="true"]'
      );
      if (element) {
        resizeObserver.observe(element);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [isActive]);

  return (
    <Flex
      sx={{
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
        width: "100%",
        position: "relative",
      }}
      flexDir={"column"}
    >
      <Text
        sx={{
          fontSize: "56px",
          fontWeight: 900,
          color: "#FFF",
          wordBreak: "break-all",
          textAlign: "center",
          marginTop: "20px",
        }}
      >
        {showBalance
          ? `${formatAmountWithFixed(
              usdToCurrency(totalUsdValue),
              usdcInfo.decimals
            )}`
          : "*****"}
      </Text>
      <Flex
        align={"center"}
        justify={"center"}
        sx={{
          marginTop: "8px",
        }}
      >
        <Text
          sx={{
            fontSize: "30px",
            fontWeight: 700,
            color: "#FFF",
            marginRight: "10px",
          }}
        >
          {currencyUnit}
        </Text>
        <CurrencySwitch onChange={handleSwitchCurrency} checked={showUsd} />
      </Flex>
      <Text
        sx={{
          color: "#FFF",
          textAlign: "center",
          fontSize: "20px",
          fontWeight: 500,
          marginTop: "16px",
        }}
      >
        Total Holdings
      </Text>

      {isActive && (
        <BottomSheet
          open
          draggable
          skipInitialTransition
          ref={sheetRef}
          blocking={false}
          snapPoints={({ maxHeight }) => [
            (maxHeight / 10) * 9,
            400,
            (maxHeight / 10) * 3,
          ]}
          expandOnContentDrag
          className={`custom-bottom-sheet ${
            overlayOpacity === 0 ? "no-radius" : ""
          }`}
          style={{
            background: "transparent !important",
          }}
        >
          <Box
            sx={{
              height: "100%",
              padding: "10px 20px",
            }}
          >
            <Text
              sx={{
                color: "#FFF",
                fontFamily: "DIN",
                fontSize: "18px",
                fontStyle: "normal",
                fontWeight: 700,
              }}
            >
              Token
            </Text>
            {balanceList.map((token, index) => (
              <Flex
                key={index}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #FFD096",
                  padding: "20px 0",
                }}
              >
                <Flex align={"center"}>
                  <Image
                    src={token.icon}
                    sx={{
                      width: "36px",
                      marginRight: "8px",
                    }}
                  ></Image>
                  <Box>
                    <Text
                      sx={{
                        color: "#FFF",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      {token.symbol}
                    </Text>{" "}
                    {showBalance ? (
                      <Text
                        sx={{
                          color: "#FFF",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "DIN-BLACK",
                            fontWeight: 700,
                          }}
                        >
                          {formatAmountWithFixed(token.balance, token.decimals)}
                        </span>
                        <span style={{ marginLeft: "2px", fontWeight: 400 }}>
                          (
                          {formatAmountWithFixed(
                            usdToCurrency(token.value),
                            usdcInfo.decimals
                          )}{" "}
                          {currencyUnit}){" "}
                        </span>
                      </Text>
                    ) : (
                      <Text
                        sx={{
                          color: "#FFF",
                          fontSize: "20px",
                          fontWeight: 500,
                          height: "14px",
                        }}
                      >
                        ******
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Flex>
            ))}
          </Box>
        </BottomSheet>
      )}
    </Flex>
  );
};

export default BalanceContent;
